import Pager from "@/helpers/Pager";
import { Option, Pagination, Resource, Statement } from "@/helpers/Interfaces";
import axios, { AxiosResponse } from "axios";
import { merge } from "lodash";
import { UploadResource } from "../../tasks/services/TasksService";
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import { $t } from "@/plugins/localization";
import fileNameConverter from "@/modules/renter/common/helpers/fileNameConverter";

export default class NotesService
{
    /**
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(pager: Pager, filter: FilterModel): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/notes', {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param propertyPublicId: string
     *
     * @returns Promise<Resource<ListItemModel>[]>
     */
    public static async getListForProperty(propertyPublicId: string): Promise<Resource<ListItemModel>[]>
    {
        return (await axios.get<Resource<ListItemModel>[]>(`admin/renters/notes/byProperty/${propertyPublicId}`)).data;
    }

    public static async getListForCustomProperty(customPropertyPublicId: string): Promise<Resource<ListItemModel>[]>
    {
        return (await axios.get<Resource<ListItemModel>[]>(`admin/renters/notes/byCustomProperty/${customPropertyPublicId}`)).data;
    }

    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/notes`, model)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/notes/${publicId}`)).data;
    }

    /**
     * @param publicId string
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(publicId: string, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/notes/${publicId}`, model)).data;
    }

    /**
     * @param publicId string
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async updateNoteFromTask(publicId: string, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/notes/updateProblem/${publicId}`, model)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Statement>
     */
    public static async remove(publicId: string): Promise<Statement>
    {
        return (await axios.delete(`admin/renters/notes/${publicId}`)).data;
    }

    /**
     * @param parentTaskPublicId string
     *
     * @returns Promise<Statement>
     */
    public static async getNotesFromTask(parentTaskPublicId: string): Promise<Resource<ListItemModel>[]>
    {
        return (await axios.get<Resource<ListItemModel>[]>(`admin/renters/notes/byTask/${parentTaskPublicId}`)).data;
    }

    /**
     * @param publicId: string
     *
     * @returns Promise<Resource<ListItemModel>[]>
     */
    public static async sendReminder(publicId: string): Promise<string>
    {
        return (await axios.get<string>(`admin/renters/notes/sendReminder/${publicId}`)).data;
    }

    /**
     * @param publicId string
     * @param resource FileResource
     *
     * @returns Promise<Statement>
     */
    public static async updatePhotos(publicId: string, resource: FileResource[]): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/notes/${publicId}/photos`, resource)).data;
    }

    /**
     * @param file File
     * @param container string
     * @param secured boolean
     *
     * @returns Promise<UploadResource>
     */
    public static async uploadPhoto(file: File, container: string, payload: any = null, secured: boolean = false): Promise<UploadResource>
    {
        const data = fileNameConverter(file, container, payload, secured);

        const result = await axios.post<UploadResource>('admin/renters/notes/uploadPhotos', data);

        return result.data;
    }

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<AxiosResponse>
     */
    public static async getRaport(filter: FilterModel, pager: Pager): Promise<AxiosResponse>
    {
        return (await axios.get<Blob>('admin/renters/notes/excel', {
            params: merge({}, filter, pager.data()),
            responseType: "blob"
        }));
    }
}

export interface UserIconModel {
    id: number,
    publicId: string,
    facePictureUrl: string,
    givenName: string,
    surname: string
}

export interface ListItemModel{
    id: number;
    publicId: string;
    content: string;
    author: UserIconModel;
    dateCreatedByUtc: string;
    property: string;
    status: string;
    coordinator: UserIconModel,
    region: string
}

export interface FilterModel{
    coordinatorPublicId: string,
    regionPublicId: string,
    propertyPublicId: string,
    searchDateStart: string,
    searchDateEnd: string,
    status: string,
    citiesIds: string[] | string;
    districtsIds: string[] | string;
}

export interface FormModel{
    content: string;
    propertyPublicId: string;
    customPropertyPublicId: string;
    coordinatorPublicId: string;
    taskPublicId: string;
    commissionPublicId: string;
    parentTaskPublicId: string;
    stocktakePublicId: string;
    photos: Array<any>;
    publicId: string;
    status: string;
}

enum StatusEnum {
    Unresolved = 'Unresolved',
    InProgress = 'InProgress',
    Resolved = 'Resolved',
    Task = 'Task'
}

const StatusOptions = (): Option[] => ([
    { value: StatusEnum.Unresolved, text: $t('[[[Nierozwiązane]]]') },
    { value: StatusEnum.InProgress, text: $t('[[[W trakcie]]]') },
    { value: StatusEnum.Resolved, text: $t('[[[Rozwiązane]]]') },
    { value: StatusEnum.Task, text: $t('[[[Zadanie]]]') }
]);

const StatusName = (value: StatusEnum): string => StatusOptions().find(s => s.value === value)?.text || StatusEnum[value];

export {
    StatusEnum,
    StatusOptions,
    StatusName
};

export interface NoteItemModel extends ListItemModel {}
